import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cpu, Wifi, Package, Zap, Gauge, Flame, Settings, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import PageSEO from '../components/PageSEO';
import StructuredData from '../components/StructuredData';

const ServicesPage = () => {
  const { t } = useLanguage();

  const ecuServices = [
    { name: t('services.ecuStage1'), icon: Gauge, description: t('services.ecuStage1Desc') },
    { name: t('services.ecuStage2'), icon: Zap, description: t('services.ecuStage2Desc') },
    { name: t('services.ecuStage3'), icon: Flame, description: t('services.ecuStage3Desc') },
    { name: t('services.ecuDpf'), icon: Settings, description: t('services.ecuDpfDesc') },
    { name: t('services.ecuEgr'), icon: Settings, description: t('services.ecuEgrDesc') },
    { name: t('services.ecuAdblue'), icon: Settings, description: t('services.ecuAdblueDesc') },
    { name: t('services.ecuPops'), icon: Flame, description: t('services.ecuPopsDesc') },
    { name: t('services.ecuCustom'), icon: Cpu, description: t('services.ecuCustomDesc') },
  ];

  const diagnosticsServices = [
    { title: t('services.diagLive'), description: t('services.diagLiveDesc') },
    { title: t('services.diagFault'), description: t('services.diagFaultDesc') },
    { title: t('services.diagEcu'), description: t('services.diagEcuDesc') },
    { title: t('services.diagRemote'), description: t('services.diagRemoteDesc') },
  ];

  const toolsServices = [
    { title: t('services.toolsFlash'), description: t('services.toolsFlashDesc') },
    { title: t('services.toolsAdvanced'), description: t('services.toolsAdvancedDesc') },
    { title: t('services.toolsIntegration'), description: t('services.toolsIntegrationDesc') },
  ];

  return (
    <div className="relative pt-20" data-testid="services-page">
      <PageSEO path="/services" />
      <StructuredData path="/services" />
      <div className="ambient-glow ambient-orange" />
      <div className="ambient-glow ambient-blue" />
      <div className="fixed top-20 left-0 right-0 glow-line z-40" />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('services.heroLabel')}</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
              {t('services.heroTitle1')}<br />
              <span className="gradient-text-orange">{t('services.heroTitle2')}</span>
            </h1>
            <p className="text-zinc-400 max-w-3xl mx-auto text-base md:text-lg">{t('services.heroDesc')}</p>
          </motion.div>
        </div>
      </section>

      {/* ECU File Services */}
      <section className="section-padding bg-[#05050A]" data-testid="ecu-services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-xl bg-[#FF5E00]/20 flex items-center justify-center">
              <Cpu className="w-8 h-8 text-[#FF5E00]" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">{t('services.ecuTitle')}</h2>
              <p className="text-zinc-400">{t('services.ecuDesc')}</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecuServices.map((service, index) => (
              <motion.div key={service.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="glass-card service-card rounded-xl p-6 group cursor-pointer">
                <service.icon className="w-10 h-10 mb-4 text-[#FF5E00] group-hover:text-[#00D4FF] transition-colors" />
                <h3 className="font-heading font-bold text-white text-lg mb-2">{service.name}</h3>
                <p className="text-zinc-500 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Remote Diagnostics */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] via-[#080810] to-[#05050A]" data-testid="diagnostics-services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-xl bg-[#00D4FF]/20 flex items-center justify-center">
              <Wifi className="w-8 h-8 text-[#00D4FF]" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">{t('services.diagTitle')}</h2>
              <p className="text-zinc-400">{t('services.diagDesc')}</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {diagnosticsServices.map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card rounded-xl p-6 flex items-start gap-4">
                <ChevronRight className="w-6 h-6 text-[#00D4FF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-white text-lg mb-2">{service.title}</h3>
                  <p className="text-zinc-500 text-sm">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Software & Tools */}
      <section className="section-padding bg-[#05050A]" data-testid="tools-services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-xl bg-[#FF5E00]/20 flex items-center justify-center">
              <Package className="w-8 h-8 text-[#FF5E00]" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">{t('services.toolsTitle')}</h2>
              <p className="text-zinc-400">{t('services.toolsDesc')}</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {toolsServices.map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card rounded-xl p-6 flex items-start gap-4">
                <ChevronRight className="w-6 h-6 text-[#FF5E00] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-white text-lg mb-2">{service.title}</h3>
                  <p className="text-zinc-500 text-sm">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t('services.ctaTitle')}</h3>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">{t('services.ctaDesc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button px-8 py-4 text-base">{t('services.ctaBtn')}</a>
              <Link to="/suite" className="secondary-button px-8 py-4 text-base">{t('services.ctaBtn2')}</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
